import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconPlus, IconTrash } from '@tabler/icons-react';

export const templateVariableTypes = [
  'string',
  'text',
  'url',
  'phone_number',
  'list',
  'url_list',
  'file',
  'file_list',
] as const;

export type TemplateVariableType = (typeof templateVariableTypes)[number];

export type TemplateVariable = {
  key: string;
  label: string;
  helpText?: string;
  type: TemplateVariableType;
  required: boolean;
  defaultValue?: string;
  isKnowledgeSource?: boolean;
};

const variableTypeOptions = templateVariableTypes.map((t) => ({
  value: t,
  label: t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
}));

const knowledgeSourceTypes = new Set<TemplateVariableType>(['url', 'url_list', 'text', 'file', 'file_list']);

function isTemplateVariableType(value: string): value is TemplateVariableType {
  return templateVariableTypes.some((t) => t === value);
}

function slugify(label: string) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function isTemplateVariable(value: unknown): value is TemplateVariable {
  if (!value || typeof value !== 'object') return false;

  const variable = value as Partial<TemplateVariable>;

  return (
    typeof variable.key === 'string' &&
    typeof variable.label === 'string' &&
    typeof variable.type === 'string' &&
    isTemplateVariableType(variable.type) &&
    typeof variable.required === 'boolean'
  );
}

export function readTemplateVariables(value: unknown): TemplateVariable[] {
  return Array.isArray(value) ? value.filter(isTemplateVariable) : [];
}

type TemplateVariableEditorProps = {
  variables: TemplateVariable[];
  onChange: (variables: TemplateVariable[]) => void;
};

export function TemplateVariableEditor({ variables, onChange }: TemplateVariableEditorProps) {
  const updateVariable = (index: number, updates: Partial<TemplateVariable>) => {
    onChange(
      variables.map((variable, i) => {
        if (i !== index) return variable;

        const merged = { ...variable, ...updates };

        return updates.type && !knowledgeSourceTypes.has(updates.type)
          ? { ...merged, isKnowledgeSource: undefined }
          : merged;
      }),
    );
  };

  const moveVariable = (index: number, direction: -1 | 1) => {
    const target = index + direction;

    if (target < 0 || target >= variables.length) return;

    const next = [...variables];
    const moved = next[index];
    const displaced = next[target];

    if (!moved || !displaced) return;

    next[index] = displaced;
    next[target] = moved;

    onChange(next);
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fw={500} size="sm">
          Template Variables
        </Text>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={() => onChange([...variables, { key: '', label: '', type: 'string', required: true }])}
        >
          Add Variable
        </Button>
      </Group>

      {variables.length === 0 && (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No variables defined. Add variables to create placeholders like {'<<companyName>>'}.
        </Text>
      )}

      {variables.map((variable, index) => (
        <Paper key={index} p="sm" withBorder>
          <Stack gap="xs">
            <Group gap="xs" align="flex-end">
              <TextInput
                label="Label"
                placeholder="e.g. Company Name"
                value={variable.label}
                onChange={(e) => updateVariable(index, { label: e.target.value, key: slugify(e.target.value) })}
                style={{ flex: 1 }}
                size="xs"
              />
              <TextInput
                label="Key"
                placeholder="e.g. company_name"
                value={variable.key}
                onChange={(e) => updateVariable(index, { key: e.target.value })}
                style={{ flex: 1 }}
                size="xs"
                ff="monospace"
              />
              <Select
                label="Type"
                data={variableTypeOptions}
                value={variable.type}
                onChange={(value) => value && isTemplateVariableType(value) && updateVariable(index, { type: value })}
                style={{ width: 140 }}
                size="xs"
              />
            </Group>
            <Group gap="xs" align="flex-end">
              <Textarea
                label="Help Text"
                placeholder="Instructions for the user"
                value={variable.helpText ?? ''}
                onChange={(e) => updateVariable(index, { helpText: e.target.value || undefined })}
                style={{ flex: 1 }}
                size="xs"
                autosize
                minRows={1}
              />
              <TextInput
                label="Default Value"
                placeholder="Optional default"
                value={variable.defaultValue ?? ''}
                onChange={(e) => updateVariable(index, { defaultValue: e.target.value || undefined })}
                style={{ flex: 1 }}
                size="xs"
              />
            </Group>
            <Group justify="space-between">
              <Group gap="md">
                <Switch
                  label="Required"
                  checked={variable.required}
                  onChange={(e) => updateVariable(index, { required: e.currentTarget.checked })}
                  size="xs"
                />
                {knowledgeSourceTypes.has(variable.type) && (
                  <Group gap={6}>
                    <Switch
                      label="Knowledge Source"
                      checked={variable.isKnowledgeSource ?? false}
                      onChange={(e) =>
                        updateVariable(index, { isKnowledgeSource: e.currentTarget.checked || undefined })
                      }
                      size="xs"
                    />
                    {variable.isKnowledgeSource && (
                      <Badge size="xs" variant="light" color="teal">
                        RAG
                      </Badge>
                    )}
                  </Group>
                )}
              </Group>
              <Group gap={4}>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => moveVariable(index, -1)}
                  disabled={index === 0}
                >
                  <IconArrowUp size={14} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => moveVariable(index, 1)}
                  disabled={index === variables.length - 1}
                >
                  <IconArrowDown size={14} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={() => onChange(variables.filter((_, i) => i !== index))}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
            </Group>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
